import { GridColDef } from "@mui/x-data-grid-premium";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { MaterialRead } from "src/store/api/generatedApi";
import { Stack, Typography } from "@mui/material";
import { UnitSuffix } from "../common/UnitSuffix";
import {
  INVENTORY_UNIT,
  RoundedInventory,
  roundedInventory,
} from "../common/inventoryTotals";
import { useIsStatic, usePeriodMaterialSummaries } from "./ProductionContext";

const INVENTORY_COLUMN_FIELDS = ["inventoryBefore", "inventoryAfter"] as const;

// Narrow enough that the two columns stay a sidebar to the recipe rather than
// competing with it: the title wraps onto two lines and the yard total takes a
// third, which is what the header's fixed height affords.
export const INVENTORY_COLUMN_WIDTH_PX = 100;

type InventoryField = (typeof INVENTORY_COLUMN_FIELDS)[number];

interface MaterialRow {
  id: number | string;
  scrap: MaterialRead;
}

// Before/after rather than initial/remaining: a period that buys scrap ends
// higher than it started (final = initial + obtained - consumed), so "remaining"
// would imply a depletion the numbers contradict.
// The column's yard total sits under its title rather than in a body row: the
// scrap column's row labels ("Total mass") describe a per-heat mix property, so
// a total belonging to the whole yard cannot borrow one without misnaming
// itself.
const InventoryColumnHeader = ({
  title,
  total,
}: {
  title: string;
  total: number;
}) => {
  const { format } = useNumberSerialiser({ decimalPlaces: 0 });
  const unit = useUnitsFormatter(false);

  return (
    <Stack sx={{ textWrap: "wrap" }} alignItems="flex-start">
      <Typography variant="body2">{title}</Typography>
      <Typography variant="body2Mono" sx={{ color: "text.secondary" }}>
        {format(total)}
        <UnitSuffix>{unit(INVENTORY_UNIT)}</UnitSuffix>
      </Typography>
    </Stack>
  );
};

// Deliberately not `PlainCell`: that paints an opaque white background, which
// reads as a patch over a tinted row. Inventory is a reference value, so the
// row's own background shows through.
const InventoryMassCell = ({ value }: { value: number }) => {
  const { format } = useNumberSerialiser({ decimalPlaces: 0 });
  const unit = useUnitsFormatter(false);

  return (
    <Stack
      sx={{ height: "100%", px: 1 }}
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
    >
      <Typography variant="body2Mono">
        {format(value)}
        <UnitSuffix>{unit(INVENTORY_UNIT)}</UnitSuffix>
      </Typography>
    </Stack>
  );
};

// The inventory each material starts and ends the period with, as two columns
// to the right of the mix columns.
//
// Static renders only — the screenshot Clark sends is the surface that wanted
// them. The interactive tables are already tight on width, and pinning them
// there to survive the horizontal scroll clipped the mix columns' own values,
// which is a poor trade for the columns people actually read.
//
// Empty too when the period carries no material summaries, so a tenant without
// inventory data keeps the table it had.
export const useInventoryColumns = <
  Row extends MaterialRow,
>(): GridColDef<Row>[] => {
  const { t } = useTenantTranslation();
  const unit = useUnitsFormatter(true);
  const periodMaterialSummaries = usePeriodMaterialSummaries();
  const isStatic = useIsStatic();

  if (!isStatic || periodMaterialSummaries.length === 0) {
    return [];
  }

  // Over every material in inventory, not just the ones this period's recipes
  // draw on, so the yard total is the yard and chains between periods rather
  // than moving with each period's material set.
  const inventory: Record<InventoryField, RoundedInventory<string>> = {
    inventoryBefore: roundedInventory({
      summaries: periodMaterialSummaries,
      material: (summary) => summary.material,
      quantity: (summary) => summary.initial_inventory,
    }),
    inventoryAfter: roundedInventory({
      summaries: periodMaterialSummaries,
      material: (summary) => summary.material,
      quantity: (summary) => summary.final_inventory,
    }),
  };

  // Aggregation rows reuse a material row's `scrap`, so only a numeric row id
  // is a real material; the rest stay blank.
  const inventoryValue = (row: Row, field: InventoryField) =>
    typeof row.id === "number"
      ? (inventory[field].byMaterial.get(row.scrap.name) ?? null)
      : null;

  return INVENTORY_COLUMN_FIELDS.map((field) => ({
    field,
    sortable: false,
    width: INVENTORY_COLUMN_WIDTH_PX,
    minWidth: INVENTORY_COLUMN_WIDTH_PX,
    flex: 0,
    headerClassName: "group--header",
    renderHeader: () => (
      <InventoryColumnHeader
        title={`${t(field)}${unit(INVENTORY_UNIT)}`}
        total={inventory[field].total}
      />
    ),
    valueGetter: (_, row: Row) => inventoryValue(row, field),
    renderCell: ({ row }) => {
      const value = inventoryValue(row, field);
      return value === null ? null : <InventoryMassCell value={value} />;
    },
  }));
};
